/** What a recorded room's Questions says and shows, worked out from the server's reading: pure, so the screen and its tests agree. */
import type { TalkAskedMoment, TalkQuestion, TalkQuestionChapter, TalkQuestionsResponse } from "../../shared/talk";
import { agoWords } from "./when";

const two = (n: number) => String(n).padStart(2, "0");

/** A second of the talk as the player counts it: "12:04", or "1:02:03" past an hour. */
export function talkClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}:${two(m)}:${two(s % 60)}` : `${two(m)}:${two(s % 60)}`;
}

/** "08:00 to 15:30", the stretch of the talk a chapter covers. */
export const chapterRange = (c: Pick<TalkQuestionChapter, "startSec" | "endSec">): string => `${talkClock(c.startSec)} to ${talkClock(c.endSec)}`;

/** How many questions wait in a chapter, grouped or not. */
export const chapterWaiting = (c: TalkQuestionChapter): number => c.single.length + c.groups.reduce((n, g) => n + g.questions.length, 0);

/** The line under a waiting question: where the asker is now, or how long ago and that it was asked while watching. */
export const askedWhen = (q: Pick<TalkQuestion, "watchingNow" | "createdAt">, now = Date.now()): string => (q.watchingNow ? "Watching now" : `${agoWords(q.createdAt, now)} · while watching`);

export const firstWord = (name: string): string => name.split(" ")[0] ?? name;

/** "Grace, Joe and Elena", the first names of the people a send reaches. */
export function namesOf(names: readonly string[]): string {
  const first = names.map(firstWord);
  if (first.length < 2) return first[0] ?? "";
  return `${first.slice(0, -1).join(", ")} and ${first.at(-1)}`;
}

/** Takes one question out of its group: it becomes a single row marked as taken out, and a group left with one is no group. */
export function takeOut(data: TalkQuestionsResponse, messageId: string): TalkQuestionsResponse {
  const chapters = data.chapters.map((c) => {
    const group = c.groups.find((g) => g.questions.some((q) => q.messageId === messageId));
    if (!group) return c;
    const question = group.questions.find((q) => q.messageId === messageId);
    if (!question) return c;
    const left = group.questions.filter((q) => q.messageId !== messageId);
    return {
      ...c,
      groups: c.groups.map((g) => (g === group ? { ...g, questions: left } : g)).filter((g) => g.questions.length > 1),
      single: [question, ...(left.length === 1 ? left : []), ...c.single],
      takenOut: [...c.takenOut, messageId],
    };
  });
  return { ...data, chapters };
}

/** Whether a moment is a poll with answers to message, a question with typed answers, or a reaction to read. */
export const momentKind = (m: TalkAskedMoment): string => (m.kind === "poll" ? "A quick poll" : m.kind === "question" ? "A quick question" : "A reaction moment");

/** The questions a sheet opened with, in the order shown: the ids that go in the send. */
export const idsOf = (questions: readonly TalkQuestion[]): string[] => questions.map((q) => q.messageId);
